/**
 * Benedictions for the "Amen moment".
 *
 * When someone taps Amen, Beloved speaks a short blessing over their prayer —
 * "a blessing, not an animation". These are the lines it may show, and a tiny
 * pure picker that rotates through them without ever repeating the same line
 * twice in a row.
 *
 * Kept as a standalone, side-effect-free module so the rotation rule is trivial
 * to unit-test.
 */

/**
 * The blessing lines shown beneath "Amen." Broad-Christian, gentle, brief.
 * @type {readonly string[]}
 */
export const BENEDICTIONS = [
  'The Lord bless you and keep you.',
  'Go in peace.',
  'May His face shine upon you.',
  'The peace of God be with you.',
  'You are beloved.',
];

/**
 * Pick the next benediction, never repeating the immediately previous one.
 *
 * Chooses uniformly at random from every line *except* the last one shown, so
 * the same blessing never appears twice running. With a single-item list (or
 * an unknown previous index) it simply returns a valid choice.
 *
 * @param {number} lastIndex - index of the line shown last (-1 / out-of-range if none).
 * @param {() => number} [rand=Math.random] - injectable RNG for deterministic tests.
 * @returns {{ index: number, line: string }} the chosen index and its text.
 */
export function pickBenediction(lastIndex, rand = Math.random) {
  const count = BENEDICTIONS.length;
  if (count === 0) return { index: -1, line: '' };
  if (count === 1) return { index: 0, line: BENEDICTIONS[0] };

  // Every index except the one we just showed.
  const choices = [];
  for (let i = 0; i < count; i += 1) {
    if (i !== lastIndex) choices.push(i);
  }

  const roll = typeof rand === 'function' ? rand() : Math.random();
  const safe = Number.isFinite(roll) ? Math.min(Math.max(roll, 0), 0.999999) : 0;
  const index = choices[Math.floor(safe * choices.length)];
  return { index, line: BENEDICTIONS[index] };
}
